import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Shield, Battery, Clock, Settings, AlertTriangle, Bell, ChevronDown, Building2, Volume2, VolumeX, CheckCircle, Wifi, WifiOff, Map } from 'lucide-react';
import ActivityChart from './ActivityChart';
import FloorPlanView from './components/FloorPlanView';

const SciFiAlarmDashboard = ({
  alarms,
  searchTerm,
  filterType,
  expandedAnlagen,
  alarmsPerFacility = 10,
  timeRange = 0,
  soundEnabled = true,
  connectionStatus = 'connected',
  lastUpdate = new Date(),
  onSearchChange,
  onFilterChange,
  onToggleAnlage,
  onAlarmsPerFacilityChange,
  onTimeRangeChange,
  onToggleSound,
  onAcknowledge,
  onAcknowledgeAll,
  onOpenFloorplanAdmin
}) => {
  const [sortType, setSortType] = useState('newest');
  const [selectedMetric, setSelectedMetric] = useState('AKTIV');
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [timeSinceUpdate, setTimeSinceUpdate] = useState('0s');
  const [activeTab, setActiveTab] = useState('alarms');
  const [selectedFacilityForPlan, setSelectedFacilityForPlan] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => (prev + 1) % 100);
    }, 50);
    setTimeout(() => setIsLoading(false), 1500);
    return () => clearInterval(timer);
  }, []);

  // Update time since last update
  useEffect(() => {
    const updateTimer = setInterval(() => {
      const seconds = Math.floor((new Date() - lastUpdate) / 1000);
      if (seconds < 60) {
        setTimeSinceUpdate(`${seconds}s`);
      } else {
        setTimeSinceUpdate(`${Math.floor(seconds / 60)}m`);
      }
    }, 1000);
    return () => clearInterval(updateTimer);
  }, [lastUpdate]);

  const stats = {
    total: alarms.length,
    active: alarms.filter(a => !a.acknowledged).length,
    warning: alarms.filter(a => a.alarmArt.includes('Alarm')).length,
    sabo: alarms.filter(a => a.alarmArt.includes('Sabot')).length,
    scharf: alarms.filter(a => a.alarmArt.includes('scharf')).length
  };

  // Bereite Daten für Charts vor
  const timelineData = alarms.slice(0, 8).map(alarm => ({
    time: new Date(alarm.timestamp).toLocaleTimeString(),
    value: alarm.alarmArt.includes('Einbruch') ? 100 : 
           alarm.alarmArt.includes('Warnung') ? 50 : 25
  })).reverse();

  const groupedAlarms = alarms.reduce((groups, alarm) => {
    const anlageId = alarm.anlage || 'Unbekannt';
    if (!groups[anlageId]) {
      groups[anlageId] = [];
    }
    if (
      (!searchTerm || 
        alarm.alarmArt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alarm.bereich.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (!filterType || alarm.alarmArt.includes(filterType))
    ) {
      groups[anlageId].push(alarm);
    }
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-[#F2E2D2] p-6">
      {/* Header mit 3D-Effekt */}
      <div className="bg-[#f7e6d8] rounded-xl p-4 mb-6 flex flex-wrap justify-between items-center gap-4 shadow-lg border border-[#e8d5c7] relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/5 before:to-transparent before:rounded-xl">
        <div className="flex items-center gap-4 relative z-10">
          <Shield className="w-8 h-8 text-[#433225]" />
          <h1 className="text-2xl font-bold text-[#433225]">PERSEC ALARM SYSTEM</h1>
          <a
            href="https://persec-technik.de"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 bg-[#e89f71] text-white text-xs font-bold rounded-full hover:bg-[#d88f61] transition-colors shadow-md"
          >
            DEMO
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-3 relative z-10">
          {/* Connection Status */}
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            connectionStatus === 'connected'
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {connectionStatus === 'connected' ? (
              <Wifi className="w-4 h-4" />
            ) : (
              <WifiOff className="w-4 h-4" />
            )}
            <span className="text-xs font-medium">
              {connectionStatus === 'connected' ? `Live (${timeSinceUpdate})` : 'Offline'}
            </span>
          </div>

          {/* Sound Toggle */}
          <button
            onClick={onToggleSound}
            className={`p-2 rounded-full transition-colors ${
              soundEnabled
                ? 'bg-[#e89f71] text-white hover:bg-[#d88f61]'
                : 'bg-[#e8d5c7] text-[#433225] hover:bg-[#dcc5b7]'
            }`}
            title={soundEnabled ? 'Sound ausschalten' : 'Sound einschalten'}
          >
            {soundEnabled ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
          </button>

          {/* Acknowledge All Button */}
          {stats.active > 0 && (
            <button
              onClick={onAcknowledgeAll}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#e89f71] text-white hover:bg-[#d88f61] transition-colors text-sm font-medium"
              title="Alle Alarme quittieren"
            >
              <CheckCircle className="w-4 h-4" />
              Alle quittieren ({stats.active})
            </button>
          )}
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Aktivitäts-Charts */}
        <ActivityChart alarms={alarms} />

        {/* Zentrale Statistik (hell) */}
        <div className="bg-[#f7e6d8] rounded-xl p-4 shadow-lg relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:rounded-xl border border-[#e8d5c7]">
          <div className="relative z-10">
            <div className="text-center mb-4">
              <div className="text-4xl font-bold text-[#433225]">{stats.active}</div>
              <div className="text-sm text-[#433225]/70">Aktive Alarme</div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2 text-[#433225]">
                  <span>System Status</span>
                  <span>Normal</span>
                </div>
                <div className="h-2 bg-[#e8d5c7] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#e89f71] rounded-full transition-all duration-500"
                    style={{ width: '85%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rechte Statistik (dunkel) */}
        <div className="bg-[#333333] rounded-xl p-4 text-[#f4d4be] shadow-xl relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/5 before:to-transparent before:rounded-xl">
          <div className="space-y-4 relative z-10">
            <div>
              <div className="flex justify-between mb-2">
                <span>Gesamtmeldungen</span>
                <span>{stats.total}</span>
              </div>
              <div className="h-2 bg-[#594335] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#e89f71] rounded-full transition-all duration-500"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span>Warnungen</span>
                <span>{stats.warning}</span>
              </div>
              <div className="h-2 bg-[#594335] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#e89f71] rounded-full transition-all duration-500"
                  style={{ width: `${(stats.warning / stats.total * 100) || 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span>Sabotage</span>
                <span>{stats.sabo}</span>
              </div>
              <div className="h-2 bg-[#594335] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#e89f71] rounded-full transition-all duration-500"
                  style={{ width: `${(stats.sabo / stats.total * 100) || 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span>Scharfschaltung</span>
                <span>{stats.scharf}</span>
              </div>
              <div className="h-2 bg-[#594335] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#e89f71] rounded-full transition-all duration-500"
                  style={{ width: `${(stats.scharf / stats.total * 100) || 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar (hell) */}
      <div className="bg-[#f7e6d8] rounded-xl p-4 mb-6 shadow-lg border border-[#e8d5c7] relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:rounded-xl">
        <div className="flex flex-wrap items-center justify-center gap-4 relative z-10 w-full max-w-3xl mx-auto">
          <input
            type="text"
            placeholder="Suche..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="px-4 py-2 rounded-full bg-white/80 text-[#333333] border-none focus:ring-2 focus:ring-[#e89f71] placeholder-[#433225]/50 shadow-sm w-48"
          />
          <select
            value={filterType}
            onChange={(e) => onFilterChange(e.target.value)}
            className="px-4 py-2 rounded-full bg-white/80 text-[#433225] border-none focus:ring-2 focus:ring-[#e89f71] shadow-sm w-40"
          >
            <option value="">Alle Typen</option>
            <option value="Einbruch">Einbruch</option>
            <option value="Warnung">Warnung</option>
            <option value="Scharf">Scharf</option>
            <option value="Sabotage">Sabotage</option>
          </select>
          {/* Time Range Filter */}
          <select
            value={timeRange}
            onChange={(e) => onTimeRangeChange(e.target.value)}
            className="px-4 py-2 rounded-full bg-white/80 text-[#433225] border-none focus:ring-2 focus:ring-[#e89f71] shadow-sm w-40"
          >
            <option value="0">Alle Zeiträume</option>
            <option value="1">Letzte 24h</option>
            <option value="7">Letzte 7 Tage</option>
            <option value="30">Letzte 30 Tage</option>
          </select>
        </div>
      </div>

      {/* Anlagen Panel mit Überschrift */}
      <div className="bg-[#f7e6d8] rounded-xl p-4 md:p-6 shadow-lg border border-[#e8d5c7] relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/10 before:to-transparent before:rounded-xl">
        {/* Panel Header mit Sortierung */}
        <div className="flex flex-col gap-3 mb-4 md:mb-6 relative z-10">
          {/* Title row */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 md:w-6 md:h-6 text-[#433225]" />
              <h2 className="text-lg md:text-xl font-bold text-[#433225]">Anlagen</h2>
              <span className="text-xs md:text-sm text-[#433225]/70">
                ({Object.keys(groupedAlarms).length})
              </span>
            </div>
            {/* Tab Navigation - always visible */}
            <div className="flex bg-[#e8d5c7] rounded-lg p-1">
              <button
                onClick={() => setActiveTab('alarms')}
                className={`px-2 md:px-3 py-1 rounded text-xs md:text-sm font-medium transition-colors ${
                  activeTab === 'alarms'
                    ? 'bg-white text-[#433225] shadow-sm'
                    : 'text-[#433225]/60 hover:text-[#433225]'
                }`}
              >
                Alarme
              </button>
              <button
                onClick={() => setActiveTab('floorplan')}
                className={`px-2 md:px-3 py-1 rounded text-xs md:text-sm font-medium transition-colors flex items-center gap-1 ${
                  activeTab === 'floorplan'
                    ? 'bg-white text-[#433225] shadow-sm'
                    : 'text-[#433225]/60 hover:text-[#433225]'
                }`}
              >
                <Map className="w-3 h-3 md:w-4 md:h-4" />
                Lageplan
              </button>
            </div>
          </div>
          {/* Filter row */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              onChange={(e) => onAlarmsPerFacilityChange(e.target.value)}
              value={alarmsPerFacility}
              className="px-3 py-2 rounded-lg bg-white/80 text-[#433225] border-none focus:ring-2 focus:ring-[#e89f71] shadow-sm text-sm min-w-0"
            >
              <option value="10">10 pro Anlage</option>
              <option value="25">25 pro Anlage</option>
              <option value="50">50 pro Anlage</option>
              <option value="100">100 pro Anlage</option>
              <option value="500">500 pro Anlage</option>
              <option value="0">Alle anzeigen</option>
            </select>
            <select
              onChange={(e) => setSortType(e.target.value)}
              value={sortType}
              className="px-3 py-2 rounded-lg bg-white/80 text-[#433225] border-none focus:ring-2 focus:ring-[#e89f71] shadow-sm text-sm min-w-0"
            >
              <option value="newest">Neueste Meldungen</option>
              <option value="alarms">Aktive Alarme zuerst</option>
              <option value="alpha">Alphabetisch (ID)</option>
            </select>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'alarms' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
          {Object.entries(groupedAlarms)
            .sort((a, b) => {
              const [idA, alarmsA] = a;
              const [idB, alarmsB] = b;
              
              switch(sortType) {
                case 'newest':
                  // Sortiere nach dem neuesten Alarm
                  const latestA = alarmsA[0]?.timestamp || '';
                  const latestB = alarmsB[0]?.timestamp || '';
                  return new Date(latestB) - new Date(latestA);
                
                case 'alarms':
                    // Sortiere nach kritischen Alarmen als letzter Meldung
                    const isAlarmA = alarmsA[0]?.alarmArt?.includes('Verlust') || 
                                    alarmsA[0]?.alarmArt?.includes('Brand') || 
                                    alarmsA[0]?.alarmArt?.includes('alarm');
                    const isAlarmB = alarmsB[0]?.alarmArt?.includes('Verlust') || 
                                    alarmsB[0]?.alarmArt?.includes('Brand') || 
                                    alarmsB[0]?.alarmArt?.includes('alarm');
                    
                    if (isAlarmA && !isAlarmB) return -1;
                    if (!isAlarmA && isAlarmB) return 1;
                    // Bei gleichem Status nach Zeitstempel sortieren
                    return new Date(alarmsB[0]?.timestamp || '') - new Date(alarmsA[0]?.timestamp || '');
                  
                
                case 'alpha':
                  // Alphabetisch nach Anlagen-ID
                  return idA.localeCompare(idB);
                
                default:
                  return 0;
              }
            })
            .map(([anlageId, alarmsForAnlage]) => {
              // Check for critical alarms
              const hasCritical = alarmsForAnlage.some(a =>
                !a.acknowledged && (
                  a.alarmArt.includes('Einbruch') ||
                  a.alarmArt.includes('Brand') ||
                  a.alarmArt.includes('Feuer') ||
                  a.alarmArt.includes('Sabotage') ||
                  a.alarmArt.includes('Überfall')
                )
              );
              const hasWarning = alarmsForAnlage.some(a =>
                !a.acknowledged && (
                  a.alarmArt.includes('Störung') ||
                  a.alarmArt.includes('Fehler') ||
                  a.alarmArt.includes('Warnung')
                )
              );
              const hasUnacknowledged = alarmsForAnlage.some(a => !a.acknowledged);

              return (
            <div key={anlageId}
                className={`bg-white/50 rounded-xl overflow-hidden shadow-md border-2 transition-all ${
                  hasCritical
                    ? 'border-red-500 animate-pulse shadow-red-200 shadow-lg'
                    : hasWarning
                      ? 'border-yellow-500 shadow-yellow-100 shadow-lg'
                      : hasUnacknowledged
                        ? 'border-[#e89f71]'
                        : 'border-[#e8d5c7]'
                }`}>
              <button
                onClick={() => onToggleAnlage(anlageId)}
                className="w-full px-4 py-3 flex flex-col"
              >
                <div className="flex items-center justify-between text-[#433225] hover:bg-[#e8d5c7] transition-colors w-full">
                  <div className="flex items-center gap-2">
                    <Shield className={`w-5 h-5 ${hasCritical ? 'text-red-500' : hasWarning ? 'text-yellow-600' : ''}`} />
                    <span className="font-medium">Anlage {anlageId}</span>
                    {hasCritical && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">
                        ALARM
                      </span>
                    )}
                    {!hasCritical && hasWarning && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-yellow-500 text-white">
                        STÖRUNG
                      </span>
                    )}
                    <span className="text-sm opacity-75">
                      ({alarmsForAnlage.length})
                    </span>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 transform transition-transform ${
                      expandedAnlagen[anlageId] ? 'rotate-180' : ''
                    }`} 
                  />
                </div>
                {/* Letzte Meldung */}
                {alarmsForAnlage.length > 0 && (
                  <div className={`mt-2 p-2 rounded-lg text-sm ${
                    alarmsForAnlage[0].alarmArt.includes('Sabotage') ||
                    alarmsForAnlage[0].alarmArt.includes('Brand') ||
                    alarmsForAnlage[0].alarmArt.includes('Alarm')
                      ? 'bg-[#C0392B] text-[#433225]'
                      : 'bg-[#e8d5c7] text-[#433225]'
                  }`}>
                    <div className="font-medium">{alarmsForAnlage[0].alarmArt}</div>
                    <div className="text-xs mt-1 opacity-75">
                      {new Date(alarmsForAnlage[0].timestamp).toLocaleString('de-DE')}
                    </div>
                  </div>
                )}
              </button>

              {expandedAnlagen[anlageId] && (
                <div className="divide-y divide-[#e8d5c7]">
                  {(alarmsPerFacility > 0 ? alarmsForAnlage.slice(0, alarmsPerFacility) : alarmsForAnlage).map((alarm, index) => (
                    <div 
                      key={index}
                      className="p-4 text-[#433225] hover:bg-[#e8d5c7] transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {alarm.alarmArt}
                          </div>
                          <div className="text-sm opacity-75">
                            Bereich: {alarm.bereich}
                          </div>
                          {alarm.komponentenNummer && (
                            <div className="text-sm opacity-75">
                              Melder: #{alarm.komponentenNummer} {alarm.komponente && alarm.komponente !== 'Unbekannt' ? `- ${alarm.komponente}` : ''}
                            </div>
                          )}
                          <div className="text-sm opacity-75">
                            {new Date(alarm.timestamp).toLocaleString('de-DE')}
                          </div>
                        </div>
                        {alarm.acknowledged ? (
                          <div className="px-2 py-1 text-xs rounded-full bg-[#e8d5c7] text-[#433225] shadow-sm">
                            Quittiert
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onAcknowledge(alarm._id);
                            }}
                            className="px-2 py-1 text-xs rounded-full bg-[#e89f71] text-white hover:bg-[#d88f61] transition-colors shadow-sm flex items-center gap-1"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Quittieren
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {alarmsPerFacility > 0 && alarmsForAnlage.length > alarmsPerFacility && (
                    <div className="p-3 text-center text-sm text-[#433225]/60 bg-[#e8d5c7]/50">
                      + {alarmsForAnlage.length - alarmsPerFacility} weitere Meldungen
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
        ) : (
          <div className="relative z-10">
            {/* Facility selector for floorplan */}
            <div className="mb-4">
              <label className="text-sm text-[#433225]/70 mr-2">Anlage auswählen:</label>
              <select
                value={selectedFacilityForPlan || ''}
                onChange={(e) => setSelectedFacilityForPlan(e.target.value)}
                className="px-4 py-2 rounded-lg bg-white text-[#433225] border border-[#e8d5c7] focus:ring-2 focus:ring-[#e89f71]"
              >
                <option value="">-- Anlage wählen --</option>
                {Object.keys(groupedAlarms).map(anlageId => (
                  <option key={anlageId} value={anlageId}>{anlageId}</option>
                ))}
              </select>
            </div>

            {selectedFacilityForPlan ? (
              <FloorPlanView
                facilityId={selectedFacilityForPlan}
                isAdmin={true}
                onEdit={() => onOpenFloorplanAdmin && onOpenFloorplanAdmin(selectedFacilityForPlan)}
              />
            ) : (
              <div className="bg-white/50 rounded-xl p-8 text-center text-[#433225]/60">
                Wähle eine Anlage um den Lageplan anzuzeigen
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SciFiAlarmDashboard;